import { trackMejoresAmigosCtaClickGA } from "@/lib/analytics";
import {
  storeMejoresAmigosCtaAttribution,
  type MejoresAmigosCtaSource,
} from "@/lib/mejoresAmigosCtaAttribution";
import {
  trackBaseTemplateClick,
  type BaseTemplateAnalyticsName,
} from "@/lib/baseTemplateAnalytics";
import { isInstagramBrowser } from "@/lib/instagramBrowserDetection";
import {
  extractMpPlanId,
  inferMejoresAmigosTier,
  isMercadoPagoCheckoutUrl,
  type MejoresAmigosTierKind,
  type MejoresAmigosTierUrls,
} from "@/lib/mejoresAmigosCtaTier";
import { buildHeroCtaSessionContext, postHeroCtaSlackFunnel } from "@/lib/heroCtaFunnel";
import {
  getPostHogSlackSessionPropertiesPostHog,
  trackMejoresAmigosCtaClickPostHog,
  trackMejoresAmigosCtaImpressionPostHog,
} from "@/lib/posthogAnalytics";
import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";

export type { MejoresAmigosCtaSource };

function buildMejoresAmigosAnalyticsExtra(params: {
  href: string;
  countryCode?: string;
  showBestFriends?: boolean;
  showBirthdayLegacy?: boolean;
  ctaResolved?: string;
  ctaGeoSource?: string;
  ctaTier: MejoresAmigosTierKind;
  mpPlanId?: string;
  tierUrls?: MejoresAmigosTierUrls;
  extra?: Record<string, string | number | boolean | undefined>;
}): Record<string, string | number | boolean | undefined> {
  return {
    ...buildHeroCtaSessionContext({
      ctaResolved: params.ctaResolved,
      ctaGeoSource: params.ctaGeoSource,
      heroCtaType: "mejores_amigos",
      href: params.href,
      isArgentina: params.countryCode === "AR",
    }),
    funnel: "mejores_amigos",
    product_type: "instagram_close_friends",
    cta_tier: params.ctaTier,
    cta_resolved: params.ctaResolved,
    cta_geo_source: params.ctaGeoSource,
    show_best_friends: params.showBestFriends ?? false,
    show_birthday_legacy: params.showBirthdayLegacy ?? false,
    payment_provider: isMercadoPagoCheckoutUrl(params.href)
      ? "mercadopago"
      : "internal",
    mp_plan_id: params.mpPlanId,
    embedded_social_browser: isInstagramBrowser(),
    ...params.extra,
  };
}

export function trackMejoresAmigosCtaClick(params: {
  source: MejoresAmigosCtaSource;
  href: string;
  label?: string;
  templateName?: string;
  elementId?: string;
  surface?: string;
  countryCode?: string;
  showBestFriends?: boolean;
  showBirthdayLegacy?: boolean;
  ctaResolved?: string;
  ctaGeoSource?: string;
  tierUrls?: MejoresAmigosTierUrls;
  extra?: Record<string, string | number | boolean | undefined>;
}): void {
  if (typeof window === "undefined") return;

  const ctaTier = inferMejoresAmigosTier(params.href, params.tierUrls);
  const mpPlanId = extractMpPlanId(params.href);
  const phCtx = getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true });
  const geo = getWorkerGeoAnalyticsProps();
  const province =
    (geo.user_province as string | undefined) ||
    (geo.province as string | undefined) ||
    undefined;
  const analyticsExtra = buildMejoresAmigosAnalyticsExtra({
    href: params.href,
    countryCode: params.countryCode,
    showBestFriends: params.showBestFriends,
    showBirthdayLegacy: params.showBirthdayLegacy,
    ctaResolved: params.ctaResolved,
    ctaGeoSource: params.ctaGeoSource,
    ctaTier,
    mpPlanId,
    tierUrls: params.tierUrls,
    extra: params.extra,
  });

  storeMejoresAmigosCtaAttribution({
    source: params.source,
    source_template: params.templateName,
    source_path: window.location.pathname,
    source_url: window.location.href,
    href: params.href,
    label: params.label,
    element_id: params.elementId,
    surface: params.surface,
    country_code: params.countryCode || (geo.country as string | undefined),
    province,
    cta_tier: ctaTier,
    cta_geo_source: params.ctaGeoSource,
    cta_resolved: params.ctaResolved,
    mp_plan_id: mpPlanId,
    ph_session_id: phCtx.ph_session_id,
  });

  trackBaseTemplateClick({
    templateName: (params.templateName ?? "BaseTemplate") as BaseTemplateAnalyticsName,
    elementId: params.elementId ?? "hero_cta_mejores_amigos",
    label: params.label ?? "Mejores Amigos",
    surface: (params.surface as "hero") ?? "hero",
    href: params.href,
    countryCode: params.countryCode,
    extra: analyticsExtra,
  });

  trackMejoresAmigosCtaClickPostHog({
    source: params.source,
    href: params.href,
    label: params.label,
    templateName: params.templateName,
    elementId: params.elementId,
    surface: params.surface,
    countryCode: params.countryCode,
    ctaTier,
    mpPlanId,
    ctaResolved: params.ctaResolved,
    ctaGeoSource: params.ctaGeoSource,
    showBestFriends: params.showBestFriends,
    showBirthdayLegacy: params.showBirthdayLegacy,
    extra: analyticsExtra,
  });

  trackMejoresAmigosCtaClickGA({
    source: params.source,
    templateName: params.templateName,
    label: params.label,
    href: params.href,
    elementId: params.elementId,
    extra: analyticsExtra as Record<string, unknown>,
  });
}

export function trackMejoresAmigosCtaImpression(params: {
  href: string;
  label?: string;
  templateName?: string;
  countryCode?: string;
  showBestFriends?: boolean;
  showBirthdayLegacy?: boolean;
  ctaResolved?: string;
  ctaGeoSource?: string;
  tierUrls?: MejoresAmigosTierUrls;
  extra?: Record<string, string | number | boolean | undefined>;
}): void {
  if (typeof window === "undefined") return;

  const ctaTier = inferMejoresAmigosTier(params.href, params.tierUrls);
  const mpPlanId = extractMpPlanId(params.href);
  const analyticsExtra = buildMejoresAmigosAnalyticsExtra({
    href: params.href,
    countryCode: params.countryCode,
    showBestFriends: params.showBestFriends,
    showBirthdayLegacy: params.showBirthdayLegacy,
    ctaResolved: params.ctaResolved,
    ctaGeoSource: params.ctaGeoSource,
    ctaTier,
    mpPlanId,
    tierUrls: params.tierUrls,
    extra: params.extra,
  });

  trackMejoresAmigosCtaImpressionPostHog({
    href: params.href,
    label: params.label,
    templateName: params.templateName,
    countryCode: params.countryCode,
    ctaTier,
    mpPlanId,
    ctaResolved: params.ctaResolved,
    ctaGeoSource: params.ctaGeoSource,
    showBestFriends: params.showBestFriends,
    showBirthdayLegacy: params.showBirthdayLegacy,
    extra: analyticsExtra,
  });

  postHeroCtaSlackFunnel("hero_cta_impression", {
    templateName: params.templateName,
    country: params.countryCode,
    heroCtaType: "mejores_amigos",
    label: params.label,
    href: params.href,
    ctaResolved: params.ctaResolved,
    ctaGeoSource: params.ctaGeoSource,
    lastStep: "hero_cta_impression",
    replayNearNow: true,
    extra: {
      cta_tier: ctaTier,
      ...(mpPlanId ? { mp_plan_id: mpPlanId } : {}),
      ...analyticsExtra,
    },
  });
}